// plugins/websocket.js
import Pusher from 'pusher-js'
import { getToken } from '@/config/token'
import { push } from 'notivue'
import { useNotificacoesStore } from '@/stores/components/notificacoes'
import Api from '@/config/api.helper.js'
import { useSaldoConta } from '@/stores/components/useSaldoConta'
import { useSessionStore } from '@/stores/components/sessionStore'
import { useInadimplenciaStore } from '@/stores/views/InadimplenciaLancamentosStore'
import router from '@/router'
import { EmpresaStatusDocumentacaoEnum } from '@/enums/EmpresaStatusDocumentacaoEnum'
import { useGeracaoArquivoStore } from '@/stores/components/geracaoArquivoStore'
import { useEmpresaStore } from '@/stores/components/empresaStore'

const websocket = {
  websocketClient: null,
  channelUser: null,
  channelCompany: null,
  channelFranquia: null,
  channelCampanha: null,

  connectWebSocket() {
    if (this.websocketClient) return

    this.websocketClient = new Pusher(
      import.meta.env.VITE_WEBSOCKETS_PUSHER_KEY,
      {
        wsHost: import.meta.env.VITE_WEBSOCKETS_URL,
        wsPort: import.meta.env.VITE_WEBSOCKETS_PORT,
        forceTLS: import.meta.env.VITE_WEBSOCKETS_FORCE_TLS === 'true',
        encrypted: true,
        disableStats: true,
        enabledTransports: ['ws', 'wss'],
        cluster: import.meta.env.VITE_WEBSOCKETS_PUSHER_CLUSTER,
        userAuthentication: {
          endpoint: `${import.meta.env.VITE_URL_API}pusher-authentication`,
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
        channelAuthorization: {
          endpoint: `${import.meta.env.VITE_URL_API_DEFAULT}broadcasting/auth`,
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        },
      }
    )

    this.websocketClient.signin()

    this.websocketClient.bind('pusher:signin_success', (data) => {
      this.subscribeToUserChannel(data)
    })
    //Pusher.logToConsole = true

    this.websocketClient.bind('pusher:signin_error', (error) => {
      console.error('Erro na autenticação:', error)
      this.disconnectWebSocket()
    })
  },

  subscribeToUserChannel(data) {
    if (!data || !this.websocketClient) return

    const channel = JSON.parse(data.user_data).channel
    const channelComp = JSON.parse(data.user_data).channelCompany
    const channelFranquia = JSON.parse(data.user_data).channelFranquia
    const channelCampanha = JSON.parse(data.user_data).channelCampanha

    if (this.channelUser) {
      this.channelUser.unbind()
    }

    this.channelUser = this.websocketClient.subscribe(`private-${channel}`)
    this.channelCompany = this.websocketClient.subscribe(`private-${channelComp}`)
    this.channelFranquia = this.websocketClient.subscribe(`private-${channelFranquia}`)
    this.channelCampanha = this.websocketClient.subscribe(`private-${channelCampanha}`)

    this.channelFranquia.bind(
      'App\\Events\\FranquiaUpdated',
      (message) => {
        const data = typeof message.data === 'string' ? JSON.parse(message.data) : message.data
        const franquias = data?.franquias ?? []
        useSessionStore().updateAssinaturaPlanoServicosCounters(franquias);
      }
    )

    this.channelCampanha.bind(
      'App\\Events\\CampanhaUpdated',
      (message) => {
        const data = typeof message.data === 'string' ? JSON.parse(message.data) : message.data
        const campanhas = data?.campanhas?.data? data.campanhas?.data : []
        console.log('Campanhas atualizadas:', campanhas);
        useSessionStore().updateCampanhasAtivas(campanhas);
      }
    )

    this.channelCompany.bind(
      'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
      (message) => {
        if (message.type === 'AtualizaSaldoEmpresa') {
          useSaldoConta().setSaldo(message.saldo)
        }
      }
    )

    this.channelUser.bind(
      'Illuminate\\Notifications\\Events\\BroadcastNotificationCreated',
      (message) => {
        if (message.type === 'UsuarioNotificacao') {
          push.info({
            message: message.mensagem,
            title: message.titulo,
            duration: 100000,
          })

          this.updateUserData()
        }

        if (message.type === 'AtualizaSaldoEmpresa') {
          useSaldoConta().setSaldo(message.saldo)
        }
      }
    )

    this.channelCompany.bind(
      'App\\Events\\StatusDocumentacaoUpdated',
      (message) => {
        const data = message.data ?? {}
        this.updateCompanyDocStatus(data)
      }
    )

    this.channelCompany.bind(
      'App\\Events\\ContratoAssinado',
      () => {
        useEmpresaStore().fetchEmpresa()
      }
    )

    this.channelCompany.bind(
      'App\\Events\\LoteImportacaoAtualizado',
      (message) => {
        const data = typeof message.data === 'string' ? JSON.parse(message.data) : message.data
        this.handleLoteImportacaoAtualizado(data ?? {})
      }
    )

    this.channelUser.bind(
      'App\\Events\\GeracaoArquivoAtualizada',
      (message) => {
        const data = typeof message.data === 'string' ? JSON.parse(message.data) : message.data
        useGeracaoArquivoStore().setGeracao(data ?? {})
      }
    )
  },

  updateUserData() {
    try {
      useNotificacoesStore().getNotificacoes()
      Api.get('v2/auth/dados-sessao-new').then((response) => {
        localStorage.setItem('userData', JSON.stringify(response.data.data))
        localStorage.setItem('user', JSON.stringify(response.data.data.usuario))
      })
    } catch (error) {
      console.error(error)
    }
  },

  handleLoteImportacaoAtualizado(data) {
    if (data.tipo !== 'LANCAMENTOSINADIMPLENCIA') return

    useInadimplenciaStore().fetchData()

    if (data.status === 'CONCLUIDO') {
      push.success({
        title: `Importação concluída: ${data.quantidade_importados} de ${data.quantidade} registros importados.`,
        duration: 30000,
      })
    } else if (data.status === 'PARCIAL') {
      push.warning({
        title: `Importação concluída com ${data.quantidade_erros} erro(s) de ${data.quantidade} linha(s).`,
        duration: 30000,
      })
    } else if (data.status === 'ERRO') {
      push.error({
        title: 'Ocorreu um erro ao processar o arquivo de importação.',
        duration: 30000,
      })
    }
  },

  async updateCompanyDocStatus(data) {
    const status = Object.values(EmpresaStatusDocumentacaoEnum).includes(data.status)
      ? data.status
      : null
    if (!status) return
    const sessionStore = useSessionStore()

    sessionStore.updateEmpresaStatusDocumentacao(status)

    try {
      await sessionStore.fetchSessionData()
      const currentPath = router.currentRoute.value.fullPath
      await router.replace(currentPath)
    } catch (error) {
      console.error('Erro ao atualizar permissões e módulos da empresa:', error)
    }
  },

  disconnectWebSocket() {
    if (this.websocketClient) {
      this.websocketClient.disconnect()
      this.websocketClient = null
      this.channelUser = null
      this.channelCompany = null
      this.channelFranquia = null
      this.channelCampanha = null
    }
  },

  isLoggedIn() {
    return Boolean(getToken())
  },

  init() {
    if (this.isLoggedIn()) {
      this.connectWebSocket()
    }
  },
}

export { websocket }

export default {
  install: (app) => {
    app.config.globalProperties.$websocket = websocket
    app.provide('websocket', websocket)
  },
}
